import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Card, Group, Text, Rating, Select } from "@mantine/core";
import { usersApi } from "../../api/users";
import type { AppUser } from "../../types/domain";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { PageHeader } from "../../components/common/PageHeader";
import { SearchInput } from "../../components/common/SearchInput";
import { DataTable, type Column } from "../../components/common/DataTable";
import { StatusBadge } from "../../components/common/StatusBadge";
import {
  ListDateRangeFilter,
  type DateRange,
} from "../../components/common/ListDateRangeFilter";
import { ExportMenu } from "../../components/common/ExportMenu";
import { TagChips, useTagCatalogue } from "../../components/tags/TagChips";
import { isActiveTag } from "../../api/tags";
import { collectAll, type ExportField } from "../../lib/export";
import { formatDate } from "../../lib/format";
import { toSelectOptions } from "../../lib/labels";
import { errorMessage } from "../../lib/axios";
import { S3Avatar } from "../../components/common/S3Avatar";

const USER_STATUS_OPTIONS = toSelectOptions(
  ["active", "suspended", "banned"],
  "All statuses",
);

const userExportFields: ExportField<AppUser>[] = [
  { label: "Name", value: (u) => u.personalDetails?.name },
  { label: "Phone", value: (u) => u.personalDetails?.phoneNumber },
  { label: "Email", value: (u) => u.personalDetails?.email },
  { label: "Rating", value: (u) => u.ratingDetails?.currentRating },
  { label: "Wallet", value: (u) => u.walletBalance },
  { label: "Status", value: (u) => u.statusDetails?.currentStatus },
  { label: "Joined", value: (u) => u.createdAt },
  {
    label: "Tags",
    value: (u) => (u.tags ?? []).filter(isActiveTag).map((t) => t.key).join("; "),
  },
];

export function UsersPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [range, setRange] = useState<DateRange>({});
  const [tag, setTag] = useState("");
  const filters = { status, tag, startDate: range.startDate, endDate: range.endDate };
  const { data: tagCatalogue } = useTagCatalogue();
  const tagOptions = [
    { value: "", label: "All tags" },
    ...(tagCatalogue ?? [])
      .filter((t) => t.appliesTo.includes("rider"))
      .map((t) => ({ value: t.key, label: t.archived ? `${t.label} (archived)` : t.label })),
  ];

  const { page, setPage, search, onSearchChange, result, isLoading, error } =
    usePaginatedList<AppUser>({
      queryKey: ["users"],
      fetcher: usersApi.list,
      filters,
    });

  const columns: Column<AppUser>[] = [
    {
      key: "name",
      header: "Rider",
      render: (u) => (
        <Group gap="sm" wrap="nowrap">
          <S3Avatar
            src={u.personalDetails?.profilePicUrl}
            radius="xl"
            size={36}
            alt={u.personalDetails?.name}
          >
            {u.personalDetails?.name?.charAt(0)?.toUpperCase() ?? "U"}
          </S3Avatar>
          <div>
            <Text size="sm" fw={600} lh={1.2}>
              {u.personalDetails?.name ?? "—"}
            </Text>
            <Text size="xs" c="dimmed">
              {u.personalDetails?.phoneNumber ?? "—"}
            </Text>
            <TagChips tags={u.tags} />
          </div>
        </Group>
      ),
    },
    {
      key: "email",
      header: "Email",
      render: (u) => u.personalDetails?.email ?? "—",
    },
    {
      key: "rating",
      header: "Rating",
      render: (u) =>
        u.ratingDetails?.currentRating ? (
          <Rating value={u.ratingDetails.currentRating} fractions={2} readOnly size="xs" />
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        ),
    },
    {
      key: "joined",
      header: "Joined",
      render: (u) => (
        <Text size="xs" c="dimmed">
          {formatDate(u.createdAt)}
        </Text>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (u) => <StatusBadge status={u.statusDetails?.currentStatus} />,
    },
  ];

  return (
    <>
      <PageHeader title="Riders" subtitle="Passenger accounts" />
      <Card withBorder radius="md" padding="lg">
        <Group mb="md" gap="sm">
          <SearchInput
            value={search}
            onChange={onSearchChange}
            placeholder="Search name, email, phone…"
          />
          <Select
            data={USER_STATUS_OPTIONS}
            value={status}
            onChange={(v) => {
              setStatus(v ?? "");
              setPage(1);
            }}
            w={150}
            allowDeselect={false}
          />
          <ListDateRangeFilter
            value={range}
            onChange={(r) => {
              setRange(r);
              setPage(1);
            }}
          />
          {tagOptions.length > 1 && (
            <Select
              data={tagOptions}
              value={tag}
              onChange={(v) => { setTag(v ?? ""); setPage(1); }}
              w={170}
              allowDeselect={false}
              placeholder="Tag"
            />
          )}
          <ExportMenu
            filename="riders"
            fields={userExportFields}
            fetchAll={() =>
              collectAll(usersApi.list, {
                search: search || undefined,
                ...filters,
              })
            }
          />
        </Group>
        <DataTable
          columns={columns}
          rows={result?.items ?? []}
          rowKey={(u) => u._id}
          loading={isLoading}
          error={error ? errorMessage(error) : null}
          emptyLabel="No riders found"
          onRowClick={(u) => navigate(`/users/${u._id}`)}
          page={page}
          totalPages={result?.totalPages}
          total={result?.total}
          onPageChange={setPage}
        />
      </Card>
    </>
  );
}
